import { Button, DatePicker, Divider, Select, Space, Steps, Typography, Upload } from 'antd'
import { type Dayjs } from 'dayjs'
import Papa from 'papaparse'
import { useMemo, useState } from 'react'

import { UploadOutlined } from '@ant-design/icons'

import { BatchTable } from './BatchTable'
import { transformHeader, validateAndFormatResults } from './csv'
import { STEPS, useBatch } from './useBatch'

const countries = ['New Zealand', 'England']

export const BatchSchoolsTab = () => {
    const {
        init,
        updateStartDate,
        updateCountry,
        country,
        rows,
        reset,
        processingStatus,
        canProcess,
        processBatch,
        verifyRows,
        setRowToVerified,
        setProcessingStatus
    } = useBatch()
    const [fields, setFields] = useState<string[]>()

    const beforeUpload = async (file: File) => {
        // supposed to be File according to docs but TypeScript doesn't like it
        Papa.parse(file as any, {
            header: true,
            transformHeader,
            complete: async results => {
                const processed = await validateAndFormatResults(results)

                setProcessingStatus(processed.isValid ? 'READY_TO_VERIFY' : 'CAN_NOT_PROCESS')
                init(processed.data)
                setFields(processed.fields)
            }
        })
        return false
    }

    const onDatePickerChange = (date: Dayjs | null) => {
        if (date) {
            updateStartDate(date.toDate())
        }
    }

    const onCountryChange = (country: string) => {
        updateCountry(country)
    }

    const currentStep = STEPS[processingStatus]
    const stepItems = [{ title: 'Upload' }, { title: 'Verification' }, { title: 'Processing' }, { title: 'Complete' }]

    const verifiedByOverride = useMemo(() => {
        if (processingStatus === 'CAN_NOT_PROCESS') {
            return rows.every(row => row.status === 'READY_TO_PROCESS')
        }
        return false
    }, [processingStatus, rows])

    return (
        <>
            <Space direction="vertical">
                <Steps current={currentStep} items={stepItems} />
                <Divider />

                {(!rows || rows.length === 0) && (
                    <>
                        <p>Must have a clean CSV prior to uploading</p>

                        <Upload accept=".csv" showUploadList={false} beforeUpload={beforeUpload}>
                            <Button icon={<UploadOutlined />}>Click to Upload</Button>
                        </Upload>
                    </>
                )}
                {fields && rows && rows.length > 0 && (
                    <>
                        <Space direction="vertical">
                            <Button
                                onClick={() => {
                                    reset()
                                }}
                            >
                                Clear
                            </Button>
                            <BatchTable rows={rows} fields={fields} onOverrideVerification={setRowToVerified} />
                        </Space>
                        <Space direction="vertical">
                            {processingStatus === 'READY_TO_VERIFY' && (
                                <>
                                    <Select placeholder="Select a country" onChange={onCountryChange}>
                                        {countries.map(c => (
                                            <Select.Option key={c} value={c}>
                                                {c}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <Button onClick={() => verifyRows(country!)} disabled={!country}>
                                        Verify Schools Can be Processed
                                    </Button>
                                </>
                            )}
                            {processingStatus === 'CAN_NOT_PROCESS' && verifiedByOverride && (
                                <>
                                    <Button onClick={() => setProcessingStatus('READY_TO_PROCESS')}>
                                        Allow schools to be processed (override the verification proccess)
                                    </Button>
                                </>
                            )}
                            {country && processingStatus === 'READY_TO_PROCESS' && (
                                <>
                                    <Typography.Title level={5}>Subscription Details</Typography.Title>
                                    <DatePicker onChange={onDatePickerChange} placeholder="Start Date" />

                                    <Button onClick={processBatch} disabled={!canProcess}>
                                        Run
                                    </Button>
                                </>
                            )}
                        </Space>
                    </>
                )}
            </Space>
        </>
    )
}
